import {
  getLocalStorage,
  callTeacherEventsApi,
  deserify,
  RouteError,
  useCommonServiceClientContext,
  getDeviceType,
  clearLocalStorageKeys,
  deleteOfflineAccessKeyFromCache,
} from '@geneo2-web/shared-ui';
import { useAppDispatch, useAppSelector,
  useDeserifiedSelector } from '../../reduxStore/reduxHooks';
import {
  resetAuthState,
  setIsLoggingOut,
} from '../../pages/Auth/reducer/auth.slice';
import { aiBookCloseEvent } from '../../pages/Home/home.events';
import { aiLogoutEvent, interactionEvent } from '../../pages/Auth/auth.events';
import {
  aiChapterCloseEvent,
  aiLessonCloseEvent,
  aiTopicCloseEvent,
} from '../../pages/Teach/teach.events';
import { aiHomeworkCloseEvent } from '../../pages/Homework/homework.events';
import { useDownloadContext } from '../../app/Context/DownloadContextProviderV2';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DeviceEnum,
  ProfileRolesEnum,
} from '@protos/user_management/ums.db_pb';
import { resetHomeDashboardState } from '../../pages/Home/reducer/homeDashboard.slice';
import { resetAssessmentFlowState } from '../../pages/Assessment/reducer/assessmentFlow.slice';
import { resetManageHWState } from '../../pages/ManageHomework/reducer/manageHomework.slice';
import { resetTeachState } from '../../pages/Teach/reducer/teach.slice';
import { resetConnectedClassState } from '../../pages/Teach/reducer/connectedClass.slice';
import { resetAnalyticsState } from '../../pages/Analytics/reducer/analytics.slice';
import { resetReportState } from '../../pages/Reports/reducer/reports.slice';
import { ENTER_UID, HOME } from '../../routeHandling/RoutesNomenclature';
import { Code, ConnectError, Interceptor } from '@bufbuild/connect';
import { CommonServiceClientContextProvider } from '@geneo2-web/shared-ui';
import { DownloadContextProviderV2 } from '../../app/Context/DownloadContextProviderV2';
import { useDispatch } from 'react-redux';

const RouteErrorBoundaryWithContext = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const navigate = useNavigate();
  const deviceType = getDeviceType();
  const userId = getLocalStorage('userId');
  const fcmToken = getLocalStorage('fcmToken');
  const deviceId = getLocalStorage('deviceID');
  const android = window.Android;

  const {
    UMSLoginAPIServiceV1ClientWithStatusCodeHandler,
    UmsUtilityAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const commonServiceClientContext = useCommonServiceClientContext();
  const { isOffline } = useDownloadContext();
  const { user_info, is_mcb_user, ssoRedirectionUrl } = deserify(
    useAppSelector((state) => state.auth)
  );
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { chapter_resources, selected_topic_info, selected_lessons_info } =
    deserify(useAppSelector((state) => state.teach));
  const { selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );

  // ---------------------------------------------------------------

  const closeAiFunctions = async (buttonName: string) => {
    await aiLessonCloseEvent({
      lessonId: selected_lessons_info?.lessonId,
      isOffline: isOffline,
    });
    await aiTopicCloseEvent({
      topicId: selected_topic_info?.topicId,
      isOffline: isOffline,
    });
    await aiChapterCloseEvent({
      chapterId: chapter_resources?.chapterId,
      isOffline: isOffline,
    });
    await aiHomeworkCloseEvent({
      homeworkId: selected_assessment_id,
      isOffline: isOffline,
    });

    await interactionEvent({
      url: pathSegments[1].replace(/ /g, '_'),
      context: 'Navigation_bar',
      name: buttonName,
      isOffline: isOffline,
    });
  };

  // ---------------------------------------------------------------

  const NotificationAPI = async () => {
    if (!userId) {
      console.log('User ID not found');
      return;
    }
    if (isOffline) return;

    try {
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        profileId: BigInt(userId),
        role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
        deviceType: DeviceEnum.DEVICE_TYPE_WEB,
        deviceToken: fcmToken?.toString(),
        isActive: false,
        deviceId: deviceId ? BigInt(deviceId) : undefined,
      });
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceToken: fcmToken?.toString(),
        topic: `${String(userId)}_${ProfileRolesEnum.PROFILE_ROLE_TEACHER}`,
        subscribeTopic: false,
      });
    } catch (err) {
      console.error('Error in Notification API:', err);
    }
  };

  // ---------------------------------------------------------------

  const onLogoutClick = async () => {
    try {
      dispatch(setIsLoggingOut(true));
      await closeAiFunctions('SIGN_OUT');
      await aiBookCloseEvent({
        bookId: class_subject_info?.bookId,
        isOffline: isOffline,
      });
      await aiLogoutEvent({ isOffline: isOffline });
      !isOffline && (await callTeacherEventsApi(commonServiceClientContext));
      if (fcmToken) {
        await NotificationAPI();
      }
      const response =
        await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.logout({
          profileId: getLocalStorage('userId'),
          role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
        });
      if (deviceType === 'android') {
        android.userLoggedOut(true);
      }
    } catch (err) {
      console.log(err);
      dispatch(setIsLoggingOut(false));
    } finally {
      let redirectUrl = undefined;
      if (is_mcb_user && ssoRedirectionUrl) {
        redirectUrl = ssoRedirectionUrl;
      }
      dispatch(resetAuthState());
      dispatch(resetHomeDashboardState());
      dispatch(resetAssessmentFlowState());
      dispatch(resetManageHWState());
      dispatch(resetTeachState());
      dispatch(resetConnectedClassState());
      dispatch(resetAnalyticsState());
      dispatch(resetReportState());
      clearLocalStorageKeys();
      deleteOfflineAccessKeyFromCache();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        navigate(ENTER_UID);
      }
    }
  };

  // ---------------------------------------------------------------

  return (
    <RouteError
      isLoggedIn={!!user_info}
      onLogoutClick={onLogoutClick}
      onHomeClick={() => navigate(HOME)}
      onLoginClick={() => navigate(ENTER_UID)}
    />
  );
};

// ===================================================================

// Minimal context provider wrapper for error boundary
const ErrorBoundaryContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const { is_logged_in_offline } = useAppSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(resetAuthState());
    dispatch(resetHomeDashboardState());
    dispatch(resetAssessmentFlowState());
    dispatch(resetManageHWState());
    dispatch(resetTeachState());
    dispatch(resetAnalyticsState());
    clearLocalStorageKeys();
    deleteOfflineAccessKeyFromCache();
  };

  const loginInterceptor: Interceptor = (next) => async (req) => {
    if (
      is_logged_in_offline &&
      ![
        'validateLoginSession',
        'validateUser',
        'teacherLoginWithUsername',
      ].includes(req.method.name)
    ) {
      throw new ConnectError('Unverified user', Code.NotFound);
    }
    return await next(req);
  };

  const headerInterceptor: Interceptor = (next) => async (req) => {
    if (process.env.NX_GENEO_FRONTEND_VERSION) {
      req.header.append(
        'X-Geneo-Version',
        process.env.NX_GENEO_FRONTEND_VERSION
      );
    }
    req.header.append('X-Application-Type', 'teacher');
    return await next(req);
  };

  const getResponseStatusCodeFunctions = () => {
    return {
      '401': handleLogout,
    };
  };

  return (
    <CommonServiceClientContextProvider
      responseStatusCodeFunction={getResponseStatusCodeFunctions()}
      interceptors={[loginInterceptor, headerInterceptor]}
    >
      <DownloadContextProviderV2>{children}</DownloadContextProviderV2>
    </CommonServiceClientContextProvider>
  );
};

// ===================================================================

// Main export - wraps in context provider to ensure context is available
export const RouteErrorBoundary = () => {
  return (
    <ErrorBoundaryContextProvider>
      <RouteErrorBoundaryWithContext />
    </ErrorBoundaryContextProvider>
  );
};

// ===================================================================
